import React, { useEffect, useState } from "react";
import api, { fmt, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Plus, Trash, X } from "@phosphor-icons/react";

export default function AssetCapitalizationPage() {
  const [rows, setRows] = useState([]);
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    asset_id: "", cap_date: new Date().toISOString().slice(0, 10),
    cost: "", depreciation_category: "",
    useful_life_years: "5", cip_account: "", notes: "",
  });

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/alm/capitalizations"), api.get("/alm/assets")]);
    setRows(a.data); setAssets(b.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/alm/capitalizations", {
      ...form,
      cost: parseFloat(form.cost),
      useful_life_years: parseFloat(form.useful_life_years || 0),
    });
    setOpen(false);
    setForm({ ...form, asset_id: "", cost: "", depreciation_category: "", cip_account: "", notes: "" });
    load();
  };

  const remove = async (id) => { if (window.confirm("Delete this capitalization?")) { await api.delete(`/alm/capitalizations/${id}`); load(); } };

  return (
    <div>
      <PageHeader
        eyebrow="Asset Lifecycle"
        title="Asset Capitalization"
        subtitle="Convert in-stock items to in-service capitalized assets with depreciation parameters."
        testid="alm-cap-title"
        actions={
          <button onClick={() => setOpen(true)} data-testid="alm-cap-new-btn" className="bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] flex items-center gap-2">
            <Plus size={14} weight="bold" /> Capitalize Asset
          </button>
        }
      />
      <div className="p-8">
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="alm-cap-table">
            <thead className="bg-gray-50">
              <tr className="text-left">
                {["Asset", "Serial", "Cap Date", "Cost", "Useful Life (Yr)", "Dep Category", "CIP Account", ""].map(h => (
                  <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs font-bold">{r.asset_tag}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.serial_number || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtDate(r.cap_date)}</td>
                  <td className="px-4 py-3 font-mono text-right">{fmt(r.cost)}</td>
                  <td className="px-4 py-3 font-mono">{r.useful_life_years}</td>
                  <td className="px-4 py-3 text-xs">{r.depreciation_category}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.cip_account}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(r.id)} data-testid={`alm-cap-del-${r.id}`} className="text-[#DC2626] hover:underline text-xs font-bold">
                      <Trash size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-500">No capitalizations yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={() => setOpen(false)}>
          <form onClick={e => e.stopPropagation()} onSubmit={create} className="w-full max-w-md bg-white h-full overflow-y-auto" data-testid="alm-cap-form">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Capitalize</div>
                <h2 className="text-2xl font-black tracking-tight">Asset Capitalization</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Asset *</label>
                <select required value={form.asset_id} onChange={e => setForm({ ...form, asset_id: e.target.value })} data-testid="alm-cap-asset" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  <option value="">— Select Asset —</option>
                  {assets.filter(a => a.status !== "retired").map(a => <option key={a.id} value={a.id}>{a.asset_tag} — {a.product_code}</option>)}
                </select>
              </div>
              {[
                ["cap_date", "Capitalization Date *", "date", true],
                ["cost", "Cost *", "number", true],
                ["useful_life_years", "Useful Life (Years)", "number", false],
                ["depreciation_category", "Depreciation Category", "text", false],
                ["cip_account", "CIP Account", "text", false],
                ["notes", "Notes", "text", false],
              ].map(([k, label, type, required]) => (
                <div key={k}>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">{label}</label>
                  <input type={type} step="any" required={required} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`alm-cap-${k}`} className="w-full border border-gray-300 px-3 py-2.5 text-sm" />
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-gray-200">
              <button type="submit" data-testid="alm-cap-submit" className="w-full bg-[#0020C2] text-white py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#001AA3]">Capitalize</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
